import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  InputAdornment,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { InfoOutlined, SearchOutlined, ShareOutlined } from "@mui/icons-material";
import { AdminFolderAccess } from "types/interfaces/adminUsers";
import CustomScrollbars from "../CustomScrollbars";
import {
  useGetAdminUserOwnedFoldersQuery,
  useGetAdminUserSharedFoldersQuery,
  useGrantFolderAccessMutation,
  useRevokeFolderAccessMutation,
} from "features/adminUsers/adminUsers";
import ShareFolderAccessModal from "./ShareFolderAccessModal";

interface Props {
  userId: number;
  isWebUser: boolean;
  canEdit: boolean;
  onSaveSuccess?: (message: string, severity: "success" | "error") => void;
}

function getFolderDisplayName(folder: AdminFolderAccess): string {
  if (folder.folder_display != null && folder.folder_display !== "") {
    return folder.folder_display;
  }
  const isMigration = !folder.is_integration && !folder.is_sftp_folder;
  const ownerName = (folder.owner_first_name || "").trim();
  if (isMigration && ownerName) {
    return `${ownerName} - ${folder.folder}`;
  }
  return folder.folder;
}

type CheckboxDisabledReason = "permission" | "web_user" | "saving";

function FolderRow({
  folder,
  showSharedChip = false,
  showCheckbox = false,
  checked = false,
  onCheckboxToggle,
  checkboxDisabled,
  checkboxDisabledReason,
  onClick,
}: {
  folder: AdminFolderAccess;
  showSharedChip?: boolean;
  showCheckbox?: boolean;
  checked?: boolean;
  onCheckboxToggle?: (folder: AdminFolderAccess) => void;
  checkboxDisabled?: boolean;
  checkboxDisabledReason?: CheckboxDisabledReason;
  onClick?: (folder: AdminFolderAccess) => void;
}) {
  const handleRowClick = () => onClick?.(folder);
  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!checkboxDisabled && onCheckboxToggle) onCheckboxToggle(folder);
  };

  const checkboxTooltipTitle =
    checkboxDisabledReason === "web_user"
      ? "Available only for web users."
      : checkboxDisabledReason === "permission"
        ? "User & Folder Manager or Super Admin required to change access."
        : checkboxDisabledReason === "saving"
          ? "Saving..."
          : "";

  const row = (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      py={0.6}
      px={0.5}
      onClick={handleRowClick}
      sx={{
        borderRadius: 1,
        cursor: onClick ? "pointer" : "default",
        "&:hover": { bgcolor: onClick ? "rgba(0,0,0,0.04)" : "rgba(0,0,0,0.02)" },
      }}
    >
      {showCheckbox && (
        <Box onClick={handleCheckboxClick} sx={{ flexShrink: 0 }}>
          {checkboxDisabled ? (
            <Tooltip title={checkboxTooltipTitle} placement="left">
              <span>
                <Checkbox checked={checked} size="small" disableRipple disabled sx={{ p: 0 }} />
              </span>
            </Tooltip>
          ) : (
            <Checkbox checked={checked} size="small" disableRipple sx={{ p: 0 }} />
          )}
        </Box>
      )}
      <Box flex={1} minWidth={0}>
        <Typography variant="body2" fontWeight={600} noWrap>
          {getFolderDisplayName(folder)}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {folder.folder_prefix}
        </Typography>
      </Box>
      <Box display="flex" gap={0.5} flexShrink={0}>
        {showSharedChip && (
          <Chip
            label="Shared"
            size="small"
            sx={{
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: "rgba(156,39,176,0.12)",
              color: "#6a1b9a",
            }}
          />
        )}
        {folder.is_sftp_folder ? (
          <Chip
            label="SFTP"
            size="small"
            sx={{
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: "rgba(21,101,192,0.10)",
              color: "#1565c0",
            }}
          />
        ) : folder.is_integration ? (
          <Chip
            label="Integration"
            size="small"
            sx={{ height: 16, fontSize: 10, fontWeight: 700, bgcolor: "#fff3e0", color: "#e65100" }}
          />
        ) : (
          <Chip
            label="Migration folder"
            size="small"
            sx={{
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              bgcolor: "#fff8e1",
              color: "#ff8f00",
            }}
          />
        )}
        {folder.sync_status !== "ready" && (
          <Chip
            label={folder.sync_status}
            size="small"
            sx={{
              height: 16,
              fontSize: 10,
              fontWeight: 700,
              bgcolor:
                folder.sync_status === "syncing"
                  ? "rgba(21,101,192,0.10)"
                  : "rgba(211,47,47,0.10)",
              color: folder.sync_status === "syncing" ? "#1565c0" : "error.dark",
            }}
          />
        )}
      </Box>
    </Box>
  );
  return onClick ? (
    <Tooltip title="Open in Data Storage" placement="left">
      {row}
    </Tooltip>
  ) : (
    row
  );
}

export default function UserManagementOwnedFoldersTab({
  userId,
  isWebUser,
  canEdit,
  onSaveSuccess,
}: Props) {
  const navigate = useNavigate();
  const { data: folders, isLoading } = useGetAdminUserOwnedFoldersQuery(userId);
  const { data: sharedFolders } = useGetAdminUserSharedFoldersQuery(userId);
  const [grant] = useGrantFolderAccessMutation();
  const [revoke] = useRevokeFolderAccessMutation();
  const [search, setSearch] = useState("");
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);

  const handleOwnedFolderAccessToggle = async (folder: AdminFolderAccess) => {
    const hasAccess = folder.has_access === true;
    setTogglingId(folder.id);
    try {
      if (hasAccess) {
        await revoke({ userId, folderId: folder.id }).unwrap();
        onSaveSuccess?.("Folder access revoked.", "success");
      } else {
        await grant({ userId, folderId: folder.id }).unwrap();
        onSaveSuccess?.("Access granted; folder will be visible on the web platform.", "success");
      }
    } catch {
      onSaveSuccess?.("Failed to update folder access.", "error");
    } finally {
      setTogglingId(null);
    }
  };

  const openFolderInStorage = (folder: AdminFolderAccess) => {
    const params = new URLSearchParams();
    if (folder.folder_prefix) params.set("prefix", folder.folder_prefix);
    navigate(`/?${params.toString()}`);
  };

  if (isLoading) {
    return (
      <Box px={2.5} pt={2}>
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} variant="text" height={40} sx={{ mb: 0.5 }} />
        ))}
      </Box>
    );
  }

  const list = folders ?? [];
  const sharedList = sharedFolders ?? [];
  const filtered = search.trim()
    ? list.filter(
        (f) =>
          getFolderDisplayName(f).toLowerCase().includes(search.toLowerCase()) ||
          f.folder_prefix.toLowerCase().includes(search.toLowerCase())
      )
    : list;

  return (
    <>
      {canEdit && (
        <Box px={2} pt={1.5} pb={1}>
          <Tooltip
            title={!isWebUser ? "Available only for web users." : ""}
            placement="top"
          >
            <span>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ShareOutlined />}
                onClick={() => isWebUser && setShareModalOpen(true)}
                disabled={!isWebUser}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: 1.5 }}
              >
                Share Folder Access
              </Button>
            </span>
          </Tooltip>
          {!isWebUser && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
              Share Folder Access and the checkboxes below are available only for web users.
            </Typography>
          )}
        </Box>
      )}

      {list.length === 0 && sharedList.length === 0 ? (
        <Box px={2.5} pt={3} display="flex" flexDirection="column" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            No folders.
          </Typography>
          {canEdit && (
            <Typography variant="caption" color="text.secondary">
              Use Share Folder Access to give this user access to other folders.
            </Typography>
          )}
        </Box>
      ) : (
        <>
          {list.length > 0 && (
            <>
              <Box px={2} pt={1} pb={0.5} display="flex" alignItems="center" gap={0.5}>
                <Typography variant="body2" color="text.secondary">
                  If the checkbox is checked, the folder will be visible to the user on the web platform.
                </Typography>
                <Tooltip title="When the box is checked, the user will see this folder on the web platform.">
                  <InfoOutlined sx={{ fontSize: 18, color: "text.secondary" }} />
                </Tooltip>
              </Box>
              <Box px={2} pt={0.5} pb={1}>
                <TextField
                  size="small"
                  placeholder="Search folders..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlined sx={{ fontSize: 16, color: "text.disabled" }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }}
                />
              </Box>
              <CustomScrollbars sx={{ flex: 1, minHeight: 0, px: 2, pb: 1 }}>
                {filtered.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" textAlign="center" mt={2}>
                    No folders match your search.
                  </Typography>
                ) : (
                  filtered.map((folder) => {
                    const disabled = !canEdit || !isWebUser || togglingId === folder.id;
                    const disabledReason: CheckboxDisabledReason | undefined = togglingId === folder.id
                      ? "saving"
                      : !isWebUser
                        ? "web_user"
                        : !canEdit
                          ? "permission"
                          : undefined;
                    return (
                      <FolderRow
                        key={folder.id}
                        folder={folder}
                        showCheckbox={true}
                        checked={folder.has_access === true}
                        onCheckboxToggle={handleOwnedFolderAccessToggle}
                        checkboxDisabled={disabled}
                        checkboxDisabledReason={disabledReason}
                        onClick={openFolderInStorage}
                      />
                    );
                  })
                )}
              </CustomScrollbars>
            </>
          )}
          {sharedList.length > 0 && (
            <Box sx={{ px: 2, pb: 2, pt: list.length > 0 ? 2 : 1 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                textTransform="uppercase"
                letterSpacing={0.6}
                display="block"
                mb={0.75}
              >
                Shared with this user ({sharedList.length})
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.25}>
                {sharedList.map((folder) => (
                  <FolderRow
                    key={folder.id}
                    folder={folder}
                    showSharedChip
                    onClick={openFolderInStorage}
                  />
                ))}
              </Box>
            </Box>
          )}
        </>
      )}

      <ShareFolderAccessModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        userId={userId}
        canEdit={canEdit}
        onSuccess={() => {
          onSaveSuccess?.("Folder access updated.", "success");
          setShareModalOpen(false);
        }}
        onError={(msg) => onSaveSuccess?.(msg, "error")}
      />
    </>
  );
}
